import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    AccountAddress,
    AppointmentResponse,
    AuctionCatalogsService,
    CreateAdminInvoice201Response,
    InvoiceDetail,
    Invoice,
    PaymentProfileCardsResponse,
    PaymentType,
    RequestInvoiceShippingRequest,
    SchedulerWithHoursData,
} from '@viewbid/ts-angular-client';
import { fmtDate, VbChipTone, VbSnackbarService } from '@viewbid/ui';
import { PaymentErrorCodes } from '../../enums/payment-error-codes';
import { PickupSlotService } from '../../services/pickup-slot.service';
import { PickupSlot } from '../../model/pickup-slot';
import { InvoicesErrorCodes } from '../../enums/invoices-error-codes';
import { InvoiceStatus } from '../../enums/invoice-statuses';
import { PendingPickupService } from 'src/app/viewbid/shared/services/pending-pickup.service';
import { isCardExpired } from 'src/app/viewbid/shared/utils/payment-utils';
import { FeatureFlagUtil } from 'src/app/core/utils/feature-flag.util';
import { FeatureFlag } from 'src/app/core/constants/feature-flag.enum';

/** The stepper's sections. Numbering is assigned over VISIBLE steps
 *  (fulfillment hides under schedule-later, payment when paid in full). */
type InvoiceStep = 'delivery' | 'fulfillment' | 'payment' | 'confirmation';

@Component({
    selector: 'vb-invoice-actions-page',
    templateUrl: './invoice-actions-page.component.html',
    styleUrls: ['./invoice-actions-page.component.scss'],
})
export class InvoiceActionsPageComponent implements OnInit {
    invoiceNumber: string = '';
    invoiceId: string = '';
    selectedDeliveryMethod: string | null = null;
    isStepOneComplete: boolean = false;
    isStepTwoComplete: boolean = false;
    isStepThreeComplete: boolean = false;
    isStepFourComplete: boolean = false;
    selectedShippingAddress: AccountAddress | null = null;
    /** FeatureFlag.PickupFulfillment — the scheduler chosen in the new
     *  step-1 UI. Drives step 2's available-slot lookup and the final
     *  booking call (createSchedulerAppointment), in place of the legacy
     *  generic-slot createPickup path. */
    selectedScheduler: SchedulerWithHoursData | null = null;
    instructions: string = '';
    isPayOnPickup: boolean = false;
    invoiceDetails: InvoiceDetail | null = null;
    paymentSuccess: boolean = false;
    private successToastId: number = 0;
    selectedPaymentCard: PaymentProfileCardsResponse | null = null;
    isLoading: boolean = false;
    /** Invoice fetch failed — renders the in-page error state (design
     *  §Loading & Error) instead of the old toast-and-redirect. */
    loadFailed: boolean = false;
    selectedSlot: PickupSlot | null = null;
    selectedDate: Date = new Date();
    public proxyName: string = '';
    public proxyTelephone: string = '';
    scheduleLater: boolean = false;
    /** Live preview of the "Pay only, pick up later" toggle — drives which
     *  steps are SHOWN (showFulfillmentStep) the instant the toggle flips,
     *  ahead of "Confirm selection". The actual committed state, and every
     *  behavioral consequence (bookings, payment gating, resets), stays on
     *  `scheduleLater`, which only changes on confirm — see
     *  onPayOnlyPreview / toggleScheduleLater. */
    scheduleLaterPreview: boolean = false;
    isPayOnlyMode: boolean = false;
    /** The single open stepper section — the parent owns the stepper. */
    openStep: InvoiceStep | null = 'delivery';
    /** Tracks which of the two legs a given confirmPayment() call kicked
     *  off (pickup booking / payment), so their success toasts can be
     *  combined into one "Payment only" / "Appt only" / "Both" message
     *  once every requested leg has settled — see settleFulfillment. */
    private fulfillmentExpected: { payment: boolean; pickup: boolean } = {
        payment: false,
        pickup: false,
    };
    private fulfillmentSettled: { payment: boolean; pickup: boolean } = {
        payment: false,
        pickup: false,
    };
    private fulfillmentSucceeded: { payment: boolean; pickup: boolean } = {
        payment: false,
        pickup: false,
    };

    constructor(
        private route: ActivatedRoute,
        private auctionCatalogsService: AuctionCatalogsService,
        private router: Router,
        public snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private pendingPickupService: PendingPickupService,
        private host: ElementRef<HTMLElement>,
        private featureFlagUtil: FeatureFlagUtil,
    ) {}

    get pickupFulfillmentEnabled(): boolean {
        return this.featureFlagUtil.isFeatureEnabled(
            FeatureFlag.PickupFulfillment,
        );
    }

    ngOnInit(): void {
        this.invoiceNumber =
            this.route.snapshot.paramMap.get('invoiceNo') || '';

        this.invoiceId = this.route.snapshot.paramMap.get('id') || '';

        this.getInvoiceDetails();
    }

    // ------------------------------------------------------------------
    // Stepper state
    // ------------------------------------------------------------------

    get isPaidInFull(): boolean {
        return !!this.invoiceDetails?.invoice?.isPaidInFull;
    }

    /** Fulfillment collapses out of the flow while schedule-later is on.
     *  Reads the live toggle preview, not the committed value, so step 2
     *  appears/disappears as soon as the toggle flips instead of waiting
     *  for Confirm selection. */
    get showFulfillmentStep(): boolean {
        return !this.scheduleLaterPreview;
    }

    /** Payment is skipped entirely when the invoice is already paid. */
    get showPaymentStep(): boolean {
        return !this.isPaidInFull;
    }

    /** Sequential numbers over the visible steps, so hidden steps leave no
     *  gaps (design §Step presentation). */
    get stepNumbers(): Record<InvoiceStep, string> {
        const numbers: Record<InvoiceStep, string> = {
            delivery: '',
            fulfillment: '',
            payment: '',
            confirmation: '',
        };
        const order: InvoiceStep[] = (
            ['delivery', 'fulfillment', 'payment', 'confirmation'] as const
        ).filter((step: InvoiceStep) => {
            if (step === 'fulfillment') return this.showFulfillmentStep;
            if (step === 'payment') return this.showPaymentStep;
            return true;
        });
        order.forEach((step: InvoiceStep, i: number) => {
            numbers[step] = String(i + 1);
        });
        return numbers;
    }

    get fulfillmentEnabled(): boolean {
        return this.isStepOneComplete;
    }

    get paymentEnabled(): boolean {
        if (this.scheduleLater) return true;
        return (
            this.isStepOneComplete &&
            (!this.showFulfillmentStep || this.isStepTwoComplete)
        );
    }

    get confirmationEnabled(): boolean {
        return this.isPaidInFull
            ? this.isStepTwoComplete
            : this.isStepThreeComplete;
    }

    /** All required steps done — gates the rail / sticky-bar Confirm. */
    get confirmReady(): boolean {
        // Going back into an earlier step disables Confirm until the user
        // works forward to the confirmation step again: while a prior step
        // is open they may be mid-change, and the recap Confirm acts on
        // could no longer be what they're looking at. Completion flags are
        // untouched — each step's own confirm re-advances the flow.
        if (this.openStep && this.openStep !== 'confirmation') return false;
        if (this.scheduleLater) return this.isStepThreeComplete;
        return (
            this.isStepOneComplete &&
            this.isStepTwoComplete &&
            (this.isPaidInFull || this.isStepThreeComplete)
        );
    }

    get confirmLabel(): string {
        if (!this.confirmReady) return 'Confirm';
        else if (this.isPayOnPickup || this.isPaidInFull)
            return 'Confirm Appointment';
        else if (!this.isPayOnlyMode || !this.scheduleLater)
            return 'Confirm Appointment & Payment';
        else return 'Confirm Payment';
    }

    get headingText(): string {
        if (this.isPaidInFull) return 'Your payment is complete';
        if (this.isPickupFlagged()) return 'Schedule your pickup';
        if (this.isPayOnlyMode) return 'Complete your payment';
        return 'Congrats on your winning bids';
    }

    /** Rail status chip — the invoices-panel vocabulary. */
    get statusChipLabel(): string {
        if (this.paymentSuccess) return 'Complete';
        if (this.isPickupFlagged()) return 'Needs scheduling';
        if (this.isPayOnlyMode) return 'Awaiting pickup';
        if (this.isPaidInFull) return 'Scheduled';
        return 'Needs scheduling';
    }

    get statusChipTone(): VbChipTone {
        if (this.paymentSuccess) return 'positive';
        if (this.isPayOnlyMode || this.isPaidInFull) return 'neutral';
        return 'warn';
    }

    get amountDue(): number {
        return this.invoiceDetails?.invoice?.amountDue ?? 0;
    }

    // Step header summaries — one-line recaps once a step is done.

    get deliverySummary(): string {
        if (this.scheduleLater) return 'Pay now · schedule pickup later';
        switch (this.selectedDeliveryMethod) {
            case 'pickup':
                return 'Pickup';
            case 'proxyPickup':
                return 'Self-service pickup';
            case 'shipping':
                return 'Shipping';
            default:
                return '';
        }
    }

    get fulfillmentSummary(): string {
        if (this.selectedDeliveryMethod === 'shipping') {
            return this.selectedShippingAddress?.address1 ?? '';
        }
        if (!this.selectedSlot) return '';
        return `${fmtDate(this.selectedDate)} ${this.selectedSlot.slotLabel} ${this.selectedSlot.zone}`;
    }

    get paymentSummary(): string {
        if (this.isPayOnPickup) return 'Pay on pickup';
        return this.selectedPaymentCard?.cardNumber ?? '';
    }

    onStepHeader(step: InvoiceStep): void {
        this.openStep = this.openStep === step ? null : step;
    }

    onStepOneComplete(complete: boolean): void {
        this.isStepOneComplete = complete;
        if (complete) {
            this.openStep = this.scheduleLater ? 'payment' : 'fulfillment';
            this.scrollToOpenStep();
        }
    }

    /** Fulfillment confirmed (Confirm pickup / shipping Next). Also fires
     *  from the child's pickup-already-scheduled auto-complete, where step
     *  one isn't done yet — bookkeep, but don't navigate the stepper. */
    onFulfillmentComplete(): void {
        this.isStepTwoComplete = true;
        if (this.isPaidInFull) {
            this.isStepThreeComplete = true;
        }
        if (this.isStepOneComplete) {
            this.openStep = this.isPaidInFull ? 'confirmation' : 'payment';
            this.scrollToOpenStep();
        }
    }

    onPaymentComplete(complete: boolean): void {
        this.isStepThreeComplete = complete;
        if (complete) {
            this.openStep = 'confirmation';
            this.scrollToOpenStep();
        }
    }

    /** After a confirm collapses a (possibly tall) step body, the page can
     *  shrink out from under the scroll position, stranding the viewport at
     *  the footer — follow the flow to the newly opened step instead. Runs
     *  on the next macrotask so the section has rendered open. Programmatic
     *  progressions only; manual header clicks don't move the page. */
    private scrollToOpenStep(): void {
        const step: InvoiceStep | null = this.openStep;
        if (!step) return;
        setTimeout(() => {
            const section: HTMLElement | null =
                this.host.nativeElement.querySelector(
                    `vb-step-section[data-step="${step}"]`,
                );
            const reduceMotion: boolean = window.matchMedia(
                '(prefers-reduced-motion: reduce)',
            ).matches;
            section?.scrollIntoView({
                behavior: reduceMotion ? 'auto' : 'smooth',
                block: 'start',
            });
        });
    }

    // ------------------------------------------------------------------
    // Data / page modes
    // ------------------------------------------------------------------

    onDeliveryMethodChange(deliveryMethod: string | null): void {
        this.selectedDeliveryMethod = deliveryMethod;
        this.isStepThreeComplete = false;
    }

    onSchedulerChange(scheduler: SchedulerWithHoursData | null): void {
        this.selectedScheduler = scheduler;
    }

    getInvoiceDetails(): void {
        this.isLoading = true;
        this.loadFailed = false;
        this.auctionCatalogsService
            .getUserInvoiceById(this.invoiceId)
            .subscribe({
                next: (response: CreateAdminInvoice201Response) => {
                    this.invoiceDetails = response.data;
                    this.determinePageMode();
                    this.isLoading = false;
                },
                error: (error: HttpErrorResponse) => {
                    const errorMessage: string =
                        error.error.errorCode ===
                        InvoicesErrorCodes.INVOICE_NOT_FOUND_ERROR
                            ? this.translate.instant(
                                  'Invoices.invoiceNotFoundError',
                              )
                            : error.error.message;

                    this.snackbarService.error(errorMessage);
                    this.isLoading = false;
                    this.loadFailed = true;
                },
            });
    }

    private determinePageMode(): void {
        const statusType: string | undefined =
            this.invoiceDetails?.currentStatus?.statusType;
        const amountDue: number = this.invoiceDetails?.invoice?.amountDue ?? 0;

        // Flagged buyers must schedule (or arrange shipping); deferral is disallowed.
        if (this.isPickupFlagged()) {
            this.isPayOnlyMode = false;
            this.scheduleLater = false;
        } else if (statusType === InvoiceStatus.PendingBuyerReview) {
            this.isPayOnlyMode = false;
            this.scheduleLater = false;
        } else if (amountDue > 0) {
            this.isPayOnlyMode = true;
            this.scheduleLater = true;
        } else {
            this.isPayOnlyMode = false;
            this.scheduleLater = false;
        }
        this.scheduleLaterPreview = this.scheduleLater;
        this.openStep = this.scheduleLater ? 'payment' : 'delivery';
    }

    isPickupFlagged(): boolean {
        const invoice: Invoice | undefined = this.invoiceDetails?.invoice;
        return !!invoice?.pickupFlaggedAt && !invoice?.forfeitureAcknowledgedAt;
    }

    /** A pickup appointment is on the books. Every warehouse status implies
     *  a booked slot (assembly only starts once a pickup is scheduled), not
     *  just 'awaiting pickup'. A pickup-flagged invoice's booking was missed
     *  and is gone — the buyer must book again, so it doesn't count. */
    hasPickupScheduled(): boolean {
        if (this.isPickupFlagged()) return false;
        const statusType: string | undefined =
            this.invoiceDetails?.currentStatus?.statusType;
        return (
            statusType === InvoiceStatus.AwaitingAssembly ||
            statusType === InvoiceStatus.AwaitingAssemblyReview ||
            statusType === InvoiceStatus.AwaitingPickup
        );
    }

    goToInvoices(): void {
        this.router.navigate(['profiles/my-profile/my-invoices']);
    }

    // ------------------------------------------------------------------
    // Confirm — API calls (unchanged destinations and payloads)
    // ------------------------------------------------------------------

    async confirmPayment(): Promise<void> {
        if (!this.confirmReady || this.isLoading) return;

        let requestingPickup: boolean = false;
        if (!this.isPayOnlyMode || !this.scheduleLater) {
            // doing either shipping or pickup
            if (
                this.selectedDeliveryMethod === 'shipping' &&
                this.selectedShippingAddress
            ) {
                this.requestShipping();
            }

            if (
                (this.selectedDeliveryMethod === 'pickup' ||
                    this.selectedDeliveryMethod === 'proxyPickup') &&
                !this.hasPickupScheduled()
            ) {
                requestingPickup = true;
            }
        }

        const requestingPayment: boolean =
            !this.isPayOnPickup && !!this.selectedPaymentCard;

        this.fulfillmentExpected = {
            payment: requestingPayment,
            pickup: requestingPickup,
        };
        this.fulfillmentSettled = { payment: false, pickup: false };
        this.fulfillmentSucceeded = { payment: false, pickup: false };

        if (requestingPickup) {
            this.createPickup();
        }

        if (requestingPayment) {
            this.makePayment();
        }

        if (this.isPayOnPickup) {
            this.paymentSuccess = true;
        }
    }

    /** Settles one leg (payment / pickup) of a confirmPayment() call and,
     *  once every leg that was actually requested has settled, shows a
     *  single combined success toast covering whichever legs succeeded
     *  (Payment only / Appt only / Both). A leg's own error toast is shown
     *  by its caller — this only ever adds a success toast. */
    private settleFulfillment(
        leg: 'payment' | 'pickup',
        succeeded: boolean,
    ): void {
        this.fulfillmentSettled[leg] = true;
        this.fulfillmentSucceeded[leg] = succeeded;

        const { payment, pickup } = this.fulfillmentExpected;
        if (payment && !this.fulfillmentSettled.payment) return;
        if (pickup && !this.fulfillmentSettled.pickup) return;

        const showPayment: boolean =
            payment && this.fulfillmentSucceeded.payment;
        const showPickup: boolean = pickup && this.fulfillmentSucceeded.pickup;
        if (!showPayment && !showPickup) return;

        const dateLabel: string = fmtDate(this.selectedDate);
        const timeLabel: string = [
            this.selectedSlot?.slotLabel,
            this.selectedSlot?.zone,
        ]
            .filter(Boolean)
            .join(' ');

        let message: string;
        if (showPayment && showPickup) {
            message = this.translate.instant(
                'Invoices.fulfillmentBothSuccess',
                {
                    date: dateLabel,
                    time: timeLabel,
                },
            );
        } else if (showPayment) {
            message = this.translate.instant(
                'Invoices.fulfillmentPaymentOnlySuccess',
                { invoiceNo: this.invoiceNumber },
            );
        } else {
            message = this.translate.instant(
                'Invoices.fulfillmentPickupOnlySuccess',
                { date: dateLabel, time: timeLabel },
            );
        }

        this.snackbarService.dismiss(this.successToastId);
        this.successToastId = this.snackbarService.success(message);
    }

    private requestShipping(): void {
        this.auctionCatalogsService
            .requestInvoiceShipping(this.invoiceId, {
                address1: this.selectedShippingAddress?.address1 ?? '',
                address2: this.selectedShippingAddress?.address2 ?? '',
                city: this.selectedShippingAddress?.city ?? '',
                province: this.selectedShippingAddress?.province ?? '',
                postalCode: this.selectedShippingAddress?.postalCode ?? '',
                country: (this.selectedShippingAddress?.country ??
                    '') as RequestInvoiceShippingRequest['country'],
                buzzer: this.selectedShippingAddress?.buzzer ?? '',
                accountAddressesId: this.selectedShippingAddress?.id ?? '',
            })
            .subscribe({
                next: () => {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant(
                            'Invoices.RequestShippingSuccess',
                        ),
                    );
                    if (this.invoiceDetails?.invoice?.isPaidInFull) {
                        this.paymentSuccess = true;
                    }
                },
                error: () => {
                    this.snackbarService.error(
                        this.translate.instant('Invoices.RequestShippingError'),
                    );
                },
            });
    }

    private async makePayment(): Promise<void> {
        if (isCardExpired(this.selectedPaymentCard?.expirationDate)) {
            this.snackbarService.error(
                this.translate.instant('Payments.cardExpired'),
            );
            return;
        }
        this.isLoading = true;
        await this.auctionCatalogsService
            .createPayment(this.invoiceId, {
                invoicesId: this.invoiceId,
                paymentTypes: this.isPayOnPickup
                    ? PaymentType.InPersonCard
                    : PaymentType.OnlineCard,
                amountPaid: this.invoiceDetails?.invoice?.amountDue ?? 0,
                customerPaymentProfileId:
                    this.selectedPaymentCard?.customerPaymentProfileId ?? '',
            })
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    if (this.invoiceDetails && this.invoiceDetails.invoice) {
                        this.invoiceDetails.invoice.amountPaid =
                            this.invoiceDetails.invoice.amountDue;
                    }
                    this.paymentSuccess = true;
                    this.settleFulfillment('payment', true);
                },
                error: (error: HttpErrorResponse) => {
                    const errorMessage: string =
                        error.error.errorCode ===
                            PaymentErrorCodes.AVS_MISMATCH &&
                        error.error.details?.errors?.length > 0
                            ? error.error.details.errors[0]
                            : error.error.message;

                    this.snackbarService.error(errorMessage);
                    this.isLoading = false;
                    this.settleFulfillment('payment', false);
                },
            });
    }

    private createPickup(): void {
        // FeatureFlag.PickupFulfillment — a scheduler was chosen in the new
        // step-1 UI, so book the appointment against it (the endpoint
        // "My appointments" actually reads from) instead of the legacy
        // generic pickup-slot booking, which never created a real
        // appointment record.
        if (this.pickupFulfillmentEnabled && this.selectedScheduler?.id) {
            this.createSchedulerAppointment(this.selectedScheduler.id);
            return;
        }
        this.auctionCatalogsService
            .createPickup(
                this.invoiceDetails?.invoice?.auctionsId ?? '',
                this.invoiceId,

                {
                    pickupTime: `${PickupSlotService.formatDateToYMD(this.selectedDate)} ${this.selectedSlot?.slotValue}`,
                    isProxyPickup:
                        this.selectedDeliveryMethod === 'proxyPickup',
                    proxyName:
                        this.selectedDeliveryMethod === 'proxyPickup'
                            ? this.proxyName
                            : `${this.invoiceDetails?.buyer?.firstname} ${this.invoiceDetails?.buyer?.lastname}`,
                    proxyTelephone:
                        this.selectedDeliveryMethod === 'proxyPickup'
                            ? `+1${this.proxyTelephone}`
                            : this.invoiceDetails?.buyer?.telephone,
                },
            )
            .subscribe({
                next: () => this.onPickupBooked(),
                error: () => {
                    this.snackbarService.error(
                        this.translate.instant(
                            'Invoices.createInvoicePickupError',
                        ),
                    );
                    this.settleFulfillment('pickup', false);
                },
            });
    }

    /** POST /schedulers/:schedulersId/appointments — books the appointment
     *  for the authenticated account on the chosen scheduler (the backend
     *  associates the account's eligible invoices itself; no invoicesId is
     *  sent). The new flow never offers self-service pickup as its own
     *  choice, so proxy fields always fall back to the buyer's own info,
     *  same as the legacy path's non-proxy branch. */
    private createSchedulerAppointment(schedulersId: string): void {
        this.auctionCatalogsService
            .createPublicAppointment(schedulersId, {
                pickupTime: `${PickupSlotService.formatDateToYMD(this.selectedDate)} ${this.selectedSlot?.slotValue}`,
                proxyName:
                    this.selectedDeliveryMethod === 'proxyPickup'
                        ? this.proxyName
                        : `${this.invoiceDetails?.buyer?.firstname} ${this.invoiceDetails?.buyer?.lastname}`,
                proxyTelephone:
                    this.selectedDeliveryMethod === 'proxyPickup'
                        ? `+1${this.proxyTelephone}`
                        : this.invoiceDetails?.buyer?.telephone,
            })
            .subscribe({
                next: (_response: AppointmentResponse) => this.onPickupBooked(),
                error: () => {
                    this.snackbarService.error(
                        this.translate.instant(
                            'Invoices.createInvoicePickupError',
                        ),
                    );
                    this.settleFulfillment('pickup', false);
                },
            });
    }

    /** Shared success bookkeeping for both booking paths above. */
    private onPickupBooked(): void {
        this.pendingPickupService.removePendingInvoice(this.invoiceId);
        if (this.invoiceDetails?.invoice?.isPaidInFull) {
            this.paymentSuccess = true;
        }
        this.settleFulfillment('pickup', true);
    }

    toggleScheduleLater($event: boolean): void {
        if (this.isPickupFlagged()) return;
        // A booked pickup (AwaitingPickup) can't be re-scheduled from here —
        // pay-only stays locked on so the fulfillment flow can't reopen
        // against the existing pickup. (Deferred-schedule invoices have no
        // pickup yet and may still leave pay-only mode.)
        if (this.hasPickupScheduled()) return;
        this.scheduleLater = $event;
        this.scheduleLaterPreview = $event;
        this.selectedPaymentCard = null;
        this.isPayOnPickup = false;
        this.selectedDeliveryMethod = null;
        this.selectedSlot = null;
        this.selectedShippingAddress = null;
        this.isStepOneComplete = false;
        this.isStepTwoComplete = false;
        this.isStepThreeComplete = false;
        this.isStepFourComplete = false;
        this.openStep = $event ? 'payment' : 'delivery';
    }

    /** FeatureFlag.PickupFulfillment — live preview of the "Pay only, pick
     *  up later" toggle, fired on every flip (not just Confirm selection),
     *  so step 2 (Fulfillment details) shows/hides immediately. Purely a
     *  display concern: `scheduleLater` itself, and everything that acts on
     *  it, only changes via toggleScheduleLater on confirm. */
    onPayOnlyPreview(checked: boolean): void {
        this.scheduleLaterPreview = checked;
    }

    onProxyTelephoneChange(telephone: string): void {
        this.proxyTelephone = telephone;
    }
}
